import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { submitCancellationReason, respondToRetentionOffer, cancelSubscription } from '../services/subscriptions';

const DESCRIPTION_MAX_LENGTH = 50;

const REASONS = [
  { value: 'too_expensive', label: 'Es muy caro / no me alcanza ahora' },
  { value: 'not_using_it', label: 'No le estoy sacando provecho' },
  { value: 'no_time', label: 'Me faltó tiempo para usarlo' },
  { value: 'missing_feature', label: 'Me faltó una función / esperaba algo distinto' },
  { value: 'no_longer_selling', label: 'Ya no vendo en Mercado Libre' },
  { value: 'other', label: 'Otro' },
];

// The discount offer's percentage depends on tier — Elite gets a deeper cut
// than Pro (each backed by its own Stripe coupon, see settings.stripe_retention_coupon_ids).
const DISCOUNT_PERCENT_BY_TIER = { pro: 30, elite: 40 };

// Copy for each retention offer, keyed the same way the backend names them
// (RetentionOffer in schemas/subscription.py) — one screen per tier, since
// the wording doesn't otherwise branch by reason within a tier.
function getOfferCopy(offer, tier) {
  if (offer === 'pause') {
    return {
      title: 'Pausa tu cuenta 1 mes gratis',
      description: 'Vuelve cuando estés listo. Tus datos te esperan.',
      accept: 'Pausar 1 mes',
    };
  }
  if (offer === 'discount') {
    const percent = DISCOUNT_PERCENT_BY_TIER[tier] || 30;
    return {
      title: `${percent}% de descuento en tu próximo mes`,
      description: 'Sigue con todo Selltrix por menos.',
      accept: `Quiero el ${percent}% OFF`,
    };
  }
  return null;
}

const TIER_INTRO = {
  entrepreneur: 'En vez de cancelar, congela tu cuenta 1 mes gratis.',
  pro: 'Antes de irte, tenemos algo para ti:',
  elite: 'Antes de irte, tenemos algo para ti:',
};

const STEP_REASON = 'reason';
const STEP_OFFER = 'offer';
const STEP_CONFIRM = 'confirm';

export default function CancellationFlowModal({ open, onOpenChange, tier, onResolved }) {
  const [step, setStep] = useState(STEP_REASON);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [attemptId, setAttemptId] = useState(null);
  const [offers, setOffers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(STEP_REASON);
    setReason('');
    setDescription('');
    setAttemptId(null);
    setOffers([]);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  // "Otro" carries no signal on its own — a description is mandatory only
  // in that case, everything else stays optional.
  const isReasonStepValid = reason && (reason !== 'other' || description.trim().length > 0);

  const handleReasonContinue = async () => {
    if (!isReasonStepValid) return;
    setSubmitting(true);
    try {
      const { attempt_id, offers: eligibleOffers } = await submitCancellationReason(reason, description);
      setAttemptId(attempt_id);
      setOffers(eligibleOffers);
      setStep(eligibleOffers.length > 0 ? STEP_OFFER : STEP_CONFIRM);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo continuar. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptOffer = async (offer) => {
    setSubmitting(true);
    try {
      await respondToRetentionOffer(attemptId, 'accept', offer);
      toast.success('¡Listo! Tu suscripción se mantiene activa.');
      handleOpenChange(false);
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aplicar la oferta');
    } finally {
      setSubmitting(false);
    }
  };

  // Used both by "Aun así, cancelar" on the offer step and by the persistent
  // "Cancelar de todas formas" link on every step — always reachable, never
  // disabled or hidden, per the compliance requirement. The one exception is
  // Paso 1 itself: a reason must be picked before *any* path forward
  // (continue or cancel) — the button stays clickable, it just won't advance
  // without one, same as "Continuar".
  const goToFinalConfirm = async () => {
    if (step === STEP_REASON && !attemptId) {
      if (!reason) {
        toast.error('Selecciona un motivo antes de continuar');
        return;
      }
      if (reason === 'other' && !description.trim()) {
        toast.error('Cuéntanos brevemente tu motivo antes de continuar');
        return;
      }
      setSubmitting(true);
      try {
        const { attempt_id } = await submitCancellationReason(reason, description);
        setAttemptId(attempt_id);
      } catch {
        // Even if this fails, don't block the user's path to cancel.
      } finally {
        setSubmitting(false);
      }
    }
    if (step === STEP_OFFER) {
      // Fire-and-forget: record the rejection for churn analytics, but don't
      // block the user on it.
      respondToRetentionOffer(attemptId, 'reject').catch(() => {});
    }
    setStep(STEP_CONFIRM);
  };

  const handleConfirmCancel = async () => {
    setSubmitting(true);
    try {
      await cancelSubscription(attemptId);
      toast.success('Tu suscripción fue cancelada. Mantendrás acceso hasta el fin del período actual.');
      handleOpenChange(false);
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cancelar la suscripción');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {step === STEP_REASON && (
          <>
            <DialogHeader>
              <DialogTitle>Antes de irte, cuéntanos por qué</DialogTitle>
              <DialogDescription>Selecciona la razón principal.</DialogDescription>
            </DialogHeader>
            <RadioGroup value={reason} onValueChange={setReason} className="py-2">
              {REASONS.map((r) => (
                <div key={r.value} className="flex items-center gap-2">
                  <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                  <Label htmlFor={`reason-${r.value}`} className="font-normal cursor-pointer">{r.label}</Label>
                </div>
              ))}
            </RadioGroup>
            <div className="space-y-1">
              <Label htmlFor="cancellation-description" className="text-xs text-muted-foreground font-normal">
                {reason === 'other' ? 'Cuéntanos brevemente tu motivo' : '¿Algo más que quieras contarnos? (opcional)'}
              </Label>
              <Textarea
                id="cancellation-description"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
                maxLength={DESCRIPTION_MAX_LENGTH}
                placeholder="Cuéntanos brevemente..."
                rows={2}
                className="resize-none text-sm"
              />
              <p className="text-[10px] text-muted-foreground/70 text-right">{description.length}/{DESCRIPTION_MAX_LENGTH}</p>
            </div>
            <DialogFooter className="flex-col sm:flex-col sm:items-stretch gap-2">
              <Button onClick={handleReasonContinue} disabled={!isReasonStepValid || submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Continuar
              </Button>
              <Button variant="link" className="text-muted-foreground" onClick={goToFinalConfirm} disabled={submitting}>
                Cancelar de todas formas
              </Button>
            </DialogFooter>
          </>
        )}

        {step === STEP_OFFER && (
          <>
            <DialogHeader>
              <DialogTitle>{TIER_INTRO[tier] || 'Antes de irte, tenemos algo para ti:'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {offers.map((offer) => {
                const copy = getOfferCopy(offer, tier);
                if (!copy) return null;
                return (
                  <div key={offer} className="border border-border rounded-lg p-4">
                    <p className="font-medium text-foreground">{copy.title}</p>
                    <p className="text-sm text-muted-foreground mb-3">{copy.description}</p>
                    <Button onClick={() => handleAcceptOffer(offer)} disabled={submitting} className="w-full">
                      {copy.accept}
                    </Button>
                  </div>
                );
              })}
            </div>
            <DialogFooter className="flex-col sm:flex-col sm:items-stretch gap-2">
              <Button variant="outline" onClick={goToFinalConfirm} disabled={submitting}>
                No, gracias — cancelar
              </Button>
              <Button variant="link" className="text-muted-foreground" onClick={goToFinalConfirm} disabled={submitting}>
                Cancelar de todas formas
              </Button>
            </DialogFooter>
          </>
        )}

        {step === STEP_CONFIRM && (
          <>
            <DialogHeader>
              <DialogTitle>¿Cancelar tu suscripción?</DialogTitle>
              <DialogDescription>
                Se cancelará tu suscripción. Perderás el acceso a tu histórico de rentabilidad real, tus análisis y la IA de publicaciones.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
                Mantener mi suscripción
              </Button>
              <Button variant="destructive" onClick={handleConfirmCancel} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Cancelar de todas formas
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
